<script setup lang="ts">
import { shareNative } from '~/shared/NativeBridge';

type Props = {
  game: Game | null | undefined;
  // Vertical placement do dropdown e da seta. Em mobile/preview o menu
  // abre pra baixo; em casos onde o botão fica colado no topo do iframe
  // pode-se forçar 'bottom' pra abrir pra cima.
  placement?: 'bottom' | 'top';
  // shareOnly: troca o ícone "..." pelo ícone de compartilhar, esconde
  // favoritar/curtir e dispara navigator.share direto (sem dropdown).
  shareOnly?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  placement: 'bottom',
  shareOnly: false,
});

const { closePreview } = useGameMobilePreview();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const { ApiService } = useApiService();
const { userId } = useUserData();
const { toggleFavorite, isFavorite, onFavoriteGamesUpdate } =
  useFavoriteGames();

const { openToaster } = useLayoutStore();
const { t } = useTexts();

const isMenuOpen = ref(false);
const isShareSubmenuOpen = ref(false);
const isSharing = ref(false);
const isFavorited = ref(false);
const userVote = ref<{ is_like: 'liked' | 'disliked' | 'unvoted' } | null>(
  null,
);

const isLiked = computed(() => userVote.value?.is_like === 'liked');

const currentGameId = computed(() => {
  const id = props.game?.id;

  return id ? Number(id) : 0;
});

const gameLink = computed(() => {
  if (!props.game?.slug) return '/';

  return `/games/${props.game.slug.toLowerCase()}`;
});

function refreshFavoriteStatus() {
  if (!isAuthenticated.value || !currentGameId.value) {
    isFavorited.value = false;

    return;
  }
  isFavorited.value = isFavorite(currentGameId.value);
}

async function refreshUserVote() {
  if (!isAuthenticated.value || !currentGameId.value) {
    userVote.value = null;

    return;
  }
  try {
    const { data } = await ApiService.get<{
      is_like: 'liked' | 'disliked' | 'unvoted';
    }>(`/casino-game-votes?casinoGameId=${currentGameId.value}`);

    userVote.value = data;
  } catch {
    userVote.value = null;
  }
}

async function handleFavorite() {
  if (!isAuthenticated.value) {
    closePreview();
    openLogin();

    return;
  }
  if (!userId.value || !currentGameId.value) return;

  const previous = isFavorited.value;

  isFavorited.value = !previous;
  try {
    const newStatus = await toggleFavorite(
      String(userId.value),
      currentGameId.value,
    );

    isFavorited.value = newStatus;
  } catch {
    isFavorited.value = previous;
  }
}

async function handleLike() {
  if (!isAuthenticated.value) {
    closePreview();
    openLogin();

    return;
  }
  if (!currentGameId.value) return;

  const wasLiked = isLiked.value;

  userVote.value = wasLiked ? null : { is_like: 'liked' };

  try {
    await ApiService.delete(
      `/casino-game-votes/destroy/${currentGameId.value}`,
      {},
    );
    if (!wasLiked) {
      await ApiService.post('/casino-game-votes/store/', {
        casinoGameId: currentGameId.value,
        is_like: 'liked',
      });
    }
    await refreshUserVote();
  } catch {
    userVote.value = wasLiked ? { is_like: 'liked' } : null;
  }
}

type SharePlatform = 'whatsapp' | 'telegram' | 'twitter' | 'facebook' | 'copy';

const shareUrl = computed(() => {
  if (!props.game?.slug) return '';

  return `${window.location.origin}${gameLink.value}`;
});

const shareText = computed(() => {
  const name = props.game?.name || '';

  return `Venha jogar ${name}!`;
});

function buildSharerUrl(platform: SharePlatform): string {
  const url = encodeURIComponent(shareUrl.value);
  const text = encodeURIComponent(shareText.value);

  switch (platform) {
    case 'whatsapp':
      return `https://api.whatsapp.com/send?text=${text}%20${url}`;

    case 'telegram':
      return `https://t.me/share/url?url=${url}&text=${text}`;

    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${url}&text=${text}`;

    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${url}`;

    default:
      return '';
  }
}

const shareSubmenuRef = ref<HTMLElement | null>(null);

function animateShareSubmenu(opening: boolean) {
  const el = shareSubmenuRef.value;

  if (!el) return;

  // Mede a altura natural do conteúdo pra animar de 0 → auto.
  const targetHeight = el.scrollHeight;
  const from = opening ? 0 : targetHeight;
  const to = opening ? targetHeight : 0;

  el.animate(
    [
      { height: `${from}px`, opacity: opening ? 0 : 1 },
      { height: `${to}px`, opacity: opening ? 1 : 0 },
    ],
    {
      duration: opening ? 350 : 300,
      easing: 'ease',
      fill: 'forwards',
    },
  );
}

function toggleShareSubmenu() {
  if (isSharing.value) return;

  const opening = !isShareSubmenuOpen.value;

  if (opening) {
    isShareSubmenuOpen.value = true;
    nextTick(() => animateShareSubmenu(true));
  } else {
    animateShareSubmenu(false);
    setTimeout(() => {
      isShareSubmenuOpen.value = false;
    }, 300);
  }
}

async function handleShareTo(platform: SharePlatform) {
  if (isSharing.value || !props.game) return;
  isSharing.value = true;

  try {
    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(shareUrl.value);
        openToaster({
          message: t('casino.game.sharing.success'),
          type: 'success',
        });
      } catch {
        openToaster({
          message: t('casino.game.sharing.not_supported'),
          type: 'error',
        });
      }
    } else {
      const webUrl = buildSharerUrl(platform);

      if (!webUrl) return;

      window.open(webUrl, '_blank', 'noopener,noreferrer');
    }
  } finally {
    isShareSubmenuOpen.value = false;
    isMenuOpen.value = false;
    setTimeout(() => {
      isSharing.value = false;
    }, 400);
  }
}

async function handleNativeShare() {
  if (isSharing.value || !props.game) return;
  isSharing.value = true;

  try {
    const outcome = await shareNative({
      title: `${props.game.name} | ${appName()}`,
      message: `Venha jogar ${props.game.name}!`,
      url: shareUrl.value || window.location.href,
    });

    if (outcome === 'navigator') {
      openToaster({
        message: t('casino.game.sharing.success'),
        type: 'success',
      });
    } else if (outcome === 'unsupported') {
      openToaster({
        message: t('casino.game.sharing.not_supported'),
        type: 'error',
      });
    }
  } finally {
    setTimeout(() => {
      isSharing.value = false;
    }, 400);
  }
}

function toggleMenu() {
  if (props.shareOnly) {
    handleNativeShare();

    return;
  }
  isMenuOpen.value = !isMenuOpen.value;
  if (!isMenuOpen.value) isShareSubmenuOpen.value = false;
}

const wrapperRef = ref<HTMLElement | null>(null);

function handleDocumentClick(event: MouseEvent) {
  const target = event.target as Node;

  if (
    isMenuOpen.value &&
    wrapperRef.value &&
    !wrapperRef.value.contains(target)
  ) {
    isMenuOpen.value = false;
    isShareSubmenuOpen.value = false;
  }
}

let unsubscribeFavorites: (() => void) | null = null;

onMounted(() => {
  unsubscribeFavorites = onFavoriteGamesUpdate(refreshFavoriteStatus);
  document.addEventListener('click', handleDocumentClick, true);
});

onBeforeUnmount(() => {
  unsubscribeFavorites?.();
  document.removeEventListener('click', handleDocumentClick, true);
});

watch(
  currentGameId,
  () => {
    refreshFavoriteStatus();
    refreshUserVote();
    isMenuOpen.value = false;
    isShareSubmenuOpen.value = false;
  },
  { immediate: true },
);

watch(isAuthenticated, () => {
  refreshFavoriteStatus();
  refreshUserVote();
});

defineExpose({
  close: () => {
    isMenuOpen.value = false;
    isShareSubmenuOpen.value = false;
  },
});
</script>

<template>
  <div
    ref="wrapperRef"
    class="am-wrapper"
    :class="`am-placement-${placement}`"
  >
    <button
      :aria-label="shareOnly ? 'Compartilhar' : 'Menu'"
      class="am-icon-btn"
      :disabled="shareOnly && isSharing"
      type="button"
      @click="toggleMenu"
    >
      <svg
        v-if="shareOnly"
        class="am-icon-share"
        fill="none"
        height="18"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        viewBox="0 0 24 24"
        width="18"
      >
        <circle
          cx="18"
          cy="5"
          r="3"
        />
        <circle
          cx="6"
          cy="12"
          r="3"
        />
        <circle
          cx="18"
          cy="19"
          r="3"
        />
        <line
          x1="8.59"
          x2="15.42"
          y1="13.51"
          y2="17.49"
        />
        <line
          x1="15.41"
          x2="8.59"
          y1="6.51"
          y2="10.49"
        />
      </svg>
      <svg
        v-else
        fill="currentColor"
        height="18"
        viewBox="0 0 20 20"
        width="18"
      >
        <circle
          cx="10"
          cy="2.5"
          r="2"
        />
        <circle
          cx="10"
          cy="10"
          r="2"
        />
        <circle
          cx="10"
          cy="17.5"
          r="2"
        />
      </svg>
    </button>

    <Transition name="am-menu">
      <div
        v-if="isMenuOpen && !shareOnly"
        class="am-menu"
      >
        <div class="am-menu-arrow" />

        <button
          class="am-menu-item"
          :class="{ 'is-active': isShareSubmenuOpen }"
          :disabled="isSharing"
          type="button"
          @click="toggleShareSubmenu"
        >
          <svg
            class="am-menu-icon"
            fill="none"
            height="16"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            viewBox="0 0 24 24"
            width="16"
          >
            <circle
              cx="18"
              cy="5"
              r="3"
            />
            <circle
              cx="6"
              cy="12"
              r="3"
            />
            <circle
              cx="18"
              cy="19"
              r="3"
            />
            <line
              x1="8.59"
              x2="15.42"
              y1="13.51"
              y2="17.49"
            />
            <line
              x1="15.41"
              x2="8.59"
              y1="6.51"
              y2="10.49"
            />
          </svg>
          <span>Compartilhar</span>
          <svg
            class="am-menu-chevron"
            :class="{ 'is-open': isShareSubmenuOpen }"
            fill="none"
            height="12"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            viewBox="0 0 24 24"
            width="12"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        <div
          v-if="isShareSubmenuOpen"
          ref="shareSubmenuRef"
          class="am-share-submenu"
        >
          <div class="am-share-submenu-inner">
            <button
              class="am-share-btn"
              :disabled="isSharing"
              title="WhatsApp"
              type="button"
              @click="handleShareTo('whatsapp')"
            >
              <svg
                fill="currentColor"
                height="18"
                viewBox="0 0 24 24"
                width="18"
              >
                <path
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"
                />
              </svg>
            </button>
            <button
              class="am-share-btn"
              :disabled="isSharing"
              title="Telegram"
              type="button"
              @click="handleShareTo('telegram')"
            >
              <svg
                fill="currentColor"
                height="18"
                viewBox="0 0 24 24"
                width="18"
              >
                <path
                  d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"
                />
              </svg>
            </button>
            <button
              class="am-share-btn"
              :disabled="isSharing"
              title="X / Twitter"
              type="button"
              @click="handleShareTo('twitter')"
            >
              <svg
                fill="currentColor"
                height="18"
                viewBox="0 0 24 24"
                width="18"
              >
                <path
                  d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                />
              </svg>
            </button>
            <button
              class="am-share-btn"
              :disabled="isSharing"
              title="Facebook"
              type="button"
              @click="handleShareTo('facebook')"
            >
              <svg
                fill="currentColor"
                height="18"
                viewBox="0 0 24 24"
                width="18"
              >
                <path
                  d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                />
              </svg>
            </button>
            <button
              class="am-share-btn"
              :disabled="isSharing"
              title="Copiar link"
              type="button"
              @click="handleShareTo('copy')"
            >
              <svg
                fill="none"
                height="18"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                viewBox="0 0 24 24"
                width="18"
              >
                <path
                  d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                />
                <path
                  d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                />
              </svg>
            </button>
          </div>
        </div>

        <button
          class="am-menu-item"
          :class="{ 'is-active': isFavorited }"
          type="button"
          @click="handleFavorite"
        >
          <svg
            class="am-menu-icon"
            :fill="isFavorited ? 'currentColor' : 'none'"
            height="16"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            viewBox="0 0 24 24"
            width="16"
          >
            <path
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
            />
          </svg>
          <span>Favoritar</span>
        </button>

        <button
          class="am-menu-item"
          :class="{ 'is-active': isLiked }"
          type="button"
          @click="handleLike"
        >
          <svg
            class="am-menu-icon"
            :fill="isLiked ? 'currentColor' : 'none'"
            height="16"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            viewBox="0 0 24 24"
            width="16"
          >
            <path
              d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"
            />
          </svg>
          <span>Curtir</span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.am-wrapper {
  position: relative;
  display: inline-block;
}

.am-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  border: none;
  cursor: pointer;
}

.am-icon-btn svg {
  width: 14px;
  height: 14px;
}

@media (min-width: 768px) {
  .am-icon-btn svg {
    width: 22px;
    height: 22px;
    overflow: visible;
  }

  /* Ajustes apenas pro ícone de "..." (3 pontinhos espaçados verticalmente).
     O ícone share (.am-icon-share) usa o tamanho padrão sem reposicionamento. */
  .am-icon-btn svg:not(.am-icon-share) circle {
    r: 3.5;
  }

  .am-icon-btn svg:not(.am-icon-share) circle:nth-of-type(1) {
    cy: -3;
  }

  .am-icon-btn svg:not(.am-icon-share) circle:nth-of-type(3) {
    cy: 23;
  }

  .am-icon-btn .am-icon-share {
    width: 16px;
    height: 16px;
  }
}

.am-icon-btn:hover {
  background: rgba(255, 255, 255, 0.16);
}

.am-menu {
  position: absolute;
  left: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 180px;
  padding: 10px;
  border-radius: 5px;
  background: #0a0f2e;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
}

.am-placement-bottom .am-menu {
  top: calc(100% + 10px);
}

.am-placement-top .am-menu {
  bottom: calc(100% + 10px);
}

.am-menu-arrow {
  position: absolute;
  left: 12px;
  width: 10px;
  height: 10px;
  background: #0a0f2e;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  transform: rotate(45deg);
  border-radius: 2px 0 0 0;
}

.am-placement-bottom .am-menu-arrow {
  top: -5px;
}

.am-placement-top .am-menu-arrow {
  bottom: -5px;
  transform: rotate(225deg);
}

.am-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.35s ease;
}

.am-menu-item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.am-menu-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.am-menu-item.is-active {
  color: #ff704a;
}

.am-menu-chevron {
  margin-left: auto;
  transition: transform 0.15s ease;
}

@media (min-width: 768px) {
  .am-menu-chevron {
    width: 18px !important;
    height: 18px !important;
  }
}

.am-menu-chevron.is-open {
  transform: rotate(180deg);
}

.am-share-submenu {
  /* Container só pra animar altura (Element.animate height: 0 → scrollHeight).
     Sem flex aqui senão os botões ficariam centralizados no espaço de 0px e
     "saltariam" do meio durante a abertura. */
  height: 0;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.am-share-submenu-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  min-width: 0;
}

.am-share-btn {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #ffffff;
  cursor: pointer;
  transition:
    background 0.15s ease,
    transform 0.1s ease;
}

.am-share-btn svg {
  width: 16px;
  height: 16px;
}

@media (min-width: 768px) {
  .am-share-submenu-inner {
    gap: 8px;
    padding: 10px;
  }

  .am-share-btn {
    height: 40px;
    padding: 0;
    border-radius: 10px;
  }

  .am-share-btn svg {
    width: 18px !important;
    height: 18px !important;
  }
}

.am-share-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.am-share-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.14);
}

.am-share-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.am-menu svg.am-menu-icon {
  flex-shrink: 0;
  display: block;
  width: 16px !important;
  height: 16px !important;
}

@media (min-width: 768px) {
  .am-menu svg.am-menu-icon {
    width: 14px !important;
    height: 14px !important;
  }

  .am-menu-item {
    font-size: 14px;
    padding: 11px 14px;
  }

  .am-menu {
    min-width: 240px;
  }
}

.am-menu-enter-active,
.am-menu-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.am-menu-enter-from,
.am-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
